import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule, NgIf, formatDate } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	ElementRef,
	HostBinding,
	Injectable,
	LOCALE_ID,
	TemplateRef,
	ViewContainerRef,
	computed,
	effect,
	inject,
	input,
	model,
	output,
	signal,
	untracked,
	viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, NativeDateAdapter, provideNativeDateAdapter } from '@angular/material/core';
import { DateRange, MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { Subscription, map, merge } from 'rxjs';

const YEARS_PER_PAGE = 24;

export interface XpDateRange {
	start: Date | null;
	end: Date | null;
}

export interface XpDateRangePreset {
	value: string;
	label: string;
}

@Injectable()
export class TwoLetterDayNamesDateAdapter extends NativeDateAdapter {
	override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
		return style === 'narrow'
			? super.getDayOfWeekNames('short').map(name => name.slice(0, 2))
			: super.getDayOfWeekNames(style);
	}
}

@Component({
	selector: 'xp-date-range-header',
	standalone: true,
	imports: [MatIconModule, NgIf],
	template: `
		<button type="button"
		        class="xp-date-range-header__arrow xp-date-range-header__previous"
		        aria-label="Previous"
		        (click)="move(-1)">
			<mat-icon>chevron_left</mat-icon>
		</button>
		<div class="xp-date-range-header__title">
			<button *ngIf="calendar.currentView === 'month'"
			        type="button"
			        class="xp-date-range-header__period"
			        (click)="calendar.currentView = 'year'">{{ monthName }}</button>
			<button type="button" class="xp-date-range-header__period" (click)="calendar.currentView = 'multi-year'">{{ yearName }}</button>
		</div>
		<button type="button"
		        class="xp-date-range-header__arrow xp-date-range-header__next"
		        aria-label="Next"
		        (click)="move(1)">
			<mat-icon>chevron_right</mat-icon>
		</button>
	`,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpDateRangeHeaderComponent<D> {
	@HostBinding('class.xp-date-range-header') readonly hostClass = true;

	protected readonly calendar = inject<MatCalendar<D>>(MatCalendar);
	private readonly dateAdapter = inject<DateAdapter<D>>(DateAdapter);

	constructor() {
		const changeDetector = inject(ChangeDetectorRef);
		merge(this.calendar.stateChanges, this.calendar.viewChanged)
			.pipe(takeUntilDestroyed())
			.subscribe(() => changeDetector.markForCheck());
	}

	protected get monthName(): string {
		return this.dateAdapter.getMonthNames('long')[this.dateAdapter.getMonth(this.calendar.activeDate)];
	}

	protected get yearName(): string {
		return this.dateAdapter.getYearName(this.calendar.activeDate);
	}

	protected move(step: number): void {
		const date = this.calendar.activeDate;
		switch (this.calendar.currentView) {
			case 'month':
				this.calendar.activeDate = this.dateAdapter.addCalendarMonths(date, step);
				break;
			case 'year':
				this.calendar.activeDate = this.dateAdapter.addCalendarYears(date, step);
				break;
			default:
				this.calendar.activeDate = this.dateAdapter.addCalendarYears(date, step * YEARS_PER_PAGE);
		}
	}
}

@Component({
	selector: 'xp-date-range',
	standalone: true,
	imports: [CommonModule, MatButtonModule, MatDatepickerModule, MatIconModule],
	providers: [provideNativeDateAdapter(), { provide: DateAdapter, useClass: TwoLetterDayNamesDateAdapter }],
	templateUrl: './xp-date-range.component.html',
	styleUrls: ['./xp-date-range.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpDateRangeComponent {
	range = model<XpDateRange>({ start: null, end: null });
	label = input<string | null>(null);
	inputId = input<string | null>(null);
	placeholder = input('');
	presets = input<XpDateRangePreset[]>([]);
	activePreset = input<string | null>(null);
	presetSelected = output<string>();

	readonly header = XpDateRangeHeaderComponent;
	readonly open = signal(false);
	readonly leftMonth = signal(startOfMonth(new Date()));
	readonly rightMonth = computed(() => addMonths(this.leftMonth(), 1));
	readonly selected = computed(() => new DateRange(this.range().start, this.range().end));
	readonly text = computed(() => {
		const { start, end } = this.range();
		if (!start) {
			return '';
		}
		return end ? `${this.format(start)} - ${this.format(end)}` : this.format(start);
	});

	private readonly overlay = inject(Overlay);
	private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
	private readonly locale = inject(LOCALE_ID);
	private readonly fieldBox = viewChild.required<ElementRef<HTMLElement>>('fieldBox');
	private readonly panelTemplate = viewChild.required<TemplateRef<unknown>>('panelTemplate');
	private readonly panelOutlet = viewChild.required('panelOutlet', { read: ViewContainerRef });
	private readonly leftCalendar = viewChild<MatCalendar<Date>>('leftCalendar');
	private readonly rightCalendar = viewChild<MatCalendar<Date>>('rightCalendar');

	private overlayRef?: OverlayRef;
	private readonly overlayEvents = new Subscription();
	/* A range this component set itself must not move the months, also when the screen writes back a copy: the user is looking at them. */
	private ownRange: XpDateRange | null = null;

	constructor() {
		effect(() => {
			const range = this.range();
			untracked(() => {
				if (!sameRange(range, this.ownRange)) {
					this.revealRange(range);
				}
			});
		}, { allowSignalWrites: true });

		effect(onCleanup => {
			const left = this.leftCalendar();
			const right = this.rightCalendar();
			if (!left || !right) {
				return;
			}
			untracked(() => this.showMonths(this.leftMonth()));
			const sync = merge(
				left.stateChanges.pipe(map(() => startOfMonth(left.activeDate))),
				right.stateChanges.pipe(map(() => addMonths(startOfMonth(right.activeDate), -1)))
			).subscribe(month => this.showMonths(month));
			onCleanup(() => sync.unsubscribe());
		}, { allowSignalWrites: true });

		inject(DestroyRef).onDestroy(() => {
			this.overlayEvents.unsubscribe();
			this.overlayRef?.dispose();
		});
	}

	openPanel(): void {
		if (this.overlayRef?.hasAttached()) {
			return;
		}
		this.overlayRef ??= this.createOverlay();
		this.overlayRef.attach(new TemplatePortal(this.panelTemplate(), this.panelOutlet()));
		this.open.set(true);
	}

	togglePanel(): void {
		if (this.overlayRef?.hasAttached()) {
			this.closePanel();
			return;
		}
		this.openPanel();
	}

	closePanel(): void {
		if (this.overlayRef?.hasAttached()) {
			this.overlayRef.detach();
		}
		this.open.set(false);
	}

	selectPreset(value: string): void {
		this.presetSelected.emit(value);
		this.closePanel();
	}

	clear(): void {
		const empty: XpDateRange = { start: null, end: null };
		this.ownRange = empty;
		this.range.set(empty);
		this.closePanel();
	}

	onDateSelected(date: Date | null): void {
		if (!date) {
			return;
		}
		const { start, end } = this.range();
		const next: XpDateRange = start && !end && date >= startOfDay(start)
			? { start, end: date }
			: { start: date, end: null };
		this.ownRange = next;
		this.range.set(next);
		if (next.end) {
			this.closePanel();
		}
	}

	private createOverlay(): OverlayRef {
		const overlayRef = this.overlay.create({
			positionStrategy: this.overlay.position()
				.flexibleConnectedTo(this.fieldBox())
				.withFlexibleDimensions(false)
				.withPush(true)
				.withPositions([
					{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
					{ originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
					{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
					{ originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' }
				]),
			scrollStrategy: this.overlay.scrollStrategies.reposition()
		});
		this.overlayEvents.add(overlayRef.outsidePointerEvents().subscribe(event => {
			if (!this.host.nativeElement.contains(event.target as Node)) {
				this.closePanel();
			}
		}));
		this.overlayEvents.add(overlayRef.keydownEvents().subscribe(event => {
			if (event.key === 'Escape') {
				event.preventDefault();
				this.closePanel();
			}
		}));
		return overlayRef;
	}

	private revealRange({ start, end }: XpDateRange): void {
		if (!start && !end) {
			return;
		}
		const endMonth = startOfMonth((end ?? start)!);
		const startMonth = start ? startOfMonth(start) : endMonth;
		this.showMonths(addMonths(startMonth, 1) >= endMonth ? startMonth : addMonths(endMonth, -1));
	}

	private showMonths(left: Date): void {
		const month = startOfMonth(left);
		if (month.getTime() !== this.leftMonth().getTime()) {
			this.leftMonth.set(month);
		}
		const leftCalendar = this.leftCalendar();
		const rightCalendar = this.rightCalendar();
		if (leftCalendar && startOfMonth(leftCalendar.activeDate).getTime() !== month.getTime()) {
			leftCalendar.activeDate = month;
		}
		if (rightCalendar && startOfMonth(rightCalendar.activeDate).getTime() !== this.rightMonth().getTime()) {
			rightCalendar.activeDate = this.rightMonth();
		}
	}

	private format(date: Date): string {
		return formatDate(date, 'MMMM d, y', this.locale);
	}
}

function sameRange(a: XpDateRange, b: XpDateRange | null): boolean {
	return b !== null && sameDay(a.start, b.start) && sameDay(a.end, b.end);
}

function sameDay(a: Date | null, b: Date | null): boolean {
	return a && b ? startOfDay(a).getTime() === startOfDay(b).getTime() : !a && !b;
}

function startOfDay(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, months: number): Date {
	return new Date(date.getFullYear(), date.getMonth() + months, 1);
}
